import * as CANNON from 'cannon-es';
import * as THREE from 'three';
import { ConvexHull } from 'three/examples/jsm/math/ConvexHull.js';
import {
  computeGeometryCenterOffset,
  isPivotEditedMesh,
} from './colliderVisualUtils';

// Helper: clamp (since Math.clamp doesn't exist by default)
Math.clamp = (num, min, max) => Math.min(Math.max(num, min), max);

/**
 * Physics World Manager
 * Full-featured Three.js + cannon-es integration with VR/AR-ready features
 * NOW WITH CENTER OF MASS OFFSET SUPPORT
 */
export class PhysicsWorld {
  constructor() {
    this.world = null;
    this.bodies = new Map();
    this.meshes = new Map();
    this.materials = new Map();
    this.debug = {
      enabled: false,
      scene: null,
      visuals: new Map(),
      material: null,
    };
    this.isEnabled = false;
    this.timeStep = 1 / 60;
    this.maxSubSteps = 3;
    this.collisionTypes = new Map();
    this.cannonDebugger = null;
    // Store initial states for resetting when exiting preview mode
    this.initialStates = new Map();

    this.init();
  }

  init() {
    this.world = new CANNON.World();
    this.setGravity(0, -9.82, 0);

    // Optimized settings
    this.world.broadphase = new CANNON.SAPBroadphase(this.world);
    this.world.solver = new CANNON.GSSolver();
    this.world.solver.iterations = 10;
    this.world.solver.tolerance = 0.01;
    this.world.allowSleep = true;
    this.world.defaultContactMaterial.contactEquationStiffness = 1e8;
    this.world.defaultContactMaterial.contactEquationRelaxation = 4;

    this.createDefaultMaterials();
  }

  createDefaultMaterials() {
    const materials = {
      Default: new CANNON.Material('Default'),
      Ice: new CANNON.Material('Ice'),
      Rubber: new CANNON.Material('Rubber'),
      Metal: new CANNON.Material('Metal'),
      Wood: new CANNON.Material('Wood'),
      Concrete: new CANNON.Material('Concrete'),
    };

    materials.Default.friction = 0.3;
    materials.Default.restitution = 0.3;
    materials.Ice.friction = 0.1;
    materials.Ice.restitution = 0.1;
    materials.Rubber.friction = 0.8;
    materials.Rubber.restitution = 0.8;
    materials.Metal.friction = 0.4;
    materials.Metal.restitution = 0.1;
    materials.Wood.friction = 0.6;
    materials.Wood.restitution = 0.2;
    materials.Concrete.friction = 0.7;
    materials.Concrete.restitution = 0.1;

    Object.entries(materials).forEach(([name, material]) => {
      this.materials.set(name, material);
    });

    this.addContactMaterials();
  }

  addContactMaterials() {
    const ice = this.materials.get('Ice');
    const rubber = this.materials.get('Rubber');
    const metal = this.materials.get('Metal');

    this.world.addContactMaterial(
      new CANNON.ContactMaterial(ice, ice, { friction: 0.05, restitution: 0.1 })
    );
    this.world.addContactMaterial(
      new CANNON.ContactMaterial(rubber, rubber, {
        friction: 1.0,
        restitution: 0.8,
      })
    );
    this.world.addContactMaterial(
      new CANNON.ContactMaterial(metal, metal, {
        friction: 0.3,
        restitution: 0.05,
      })
    );
  }

  setEnabled(enabled) {
    this.isEnabled = enabled;
    if (!enabled) this.clearAllBodies();
    else if (!this.world) this.init();
  }

  update(deltaTime) {
    if (!this.isEnabled || !this.world) return;
    this.world.step(this.timeStep, deltaTime, this.maxSubSteps);
    this.syncBodiesWithMeshes();
  }

  syncBodiesWithMeshes() {
    this.bodies.forEach((body, objectId) => {
      const mesh = this.meshes.get(objectId);
      if (!mesh || !body) return;

      // Verify mesh is still in scene (might have been removed)
      if (!mesh.parent && mesh !== this.debug.scene) {
        // Mesh might have been removed from scene, skip sync
        return;
      }

      // Check if body has center of mass offset
      const centerOfMassOffset = body.userData?.centerOfMassOffset;

      // Calculate target world position
      let worldPos = new THREE.Vector3();
      if (centerOfMassOffset) {
        // Compute world-space offset from the body's quaternion
        const worldOffset = new CANNON.Vec3();
        body.quaternion.vmult(centerOfMassOffset, worldOffset);
        worldPos.set(
          body.position.x + worldOffset.x,
          body.position.y + worldOffset.y,
          body.position.z + worldOffset.z
        );
      } else {
        worldPos.set(body.position.x, body.position.y, body.position.z);
      }

      // Calculate target world rotation
      const worldQuat = new THREE.Quaternion(
        body.quaternion.x,
        body.quaternion.y,
        body.quaternion.z,
        body.quaternion.w
      );

      // Convert world position/rotation to local space if mesh has a parent
      if (mesh.parent && mesh.parent.type !== 'Scene') {
        // Get parent's world matrix inverse to convert world to local
        const parentInverse = new THREE.Matrix4();
        parentInverse.copy(mesh.parent.matrixWorld).invert();
        worldPos.applyMatrix4(parentInverse);

        // Convert world quaternion to local quaternion
        const parentWorldQuat = new THREE.Quaternion();
        mesh.parent.getWorldQuaternion(parentWorldQuat);
        parentWorldQuat.invert();
        worldQuat.premultiply(parentWorldQuat);
      }

      // Apply position and rotation directly
      mesh.position.copy(worldPos);
      mesh.quaternion.copy(worldQuat);

      // CRITICAL: Force matrix update to make changes visible in preview mode
      // This is essential when matrixAutoUpdate is false
      mesh.updateMatrix();
      mesh.updateMatrixWorld(true);

      // Update debug mesh if it exists
      const debugMesh = this.debug.visuals.get(objectId);
      if (debugMesh) {
        debugMesh.position.set(
          body.position.x,
          body.position.y,
          body.position.z
        );
        debugMesh.quaternion.set(
          body.quaternion.x,
          body.quaternion.y,
          body.quaternion.z,
          body.quaternion.w
        );
        debugMesh.updateMatrix();
        debugMesh.updateMatrixWorld(true);
      }
    });
  }

  enableDebug(scene) {
    if (!scene) return;
    this.debug.enabled = true;
    this.debug.scene = scene;
    if (!this.debug.material) {
      this.debug.material = new THREE.MeshBasicMaterial({
        color: 0x87ceeb,
        wireframe: true,
        transparent: true,
        opacity: 0.5,
        depthWrite: false,
      });
    }
    this.bodies.forEach((_, id) => {
      const mesh = this.meshes.get(id);
      if (mesh && !this.debug.visuals.has(id))
        this._createDebugForObject(id, mesh);
    });
  }

  disableDebug() {
    this.debug.enabled = false;
    if (this.debug.scene) {
      this.debug.visuals.forEach((v) => {
        try {
          this.debug.scene.remove(v);
        } catch (e) {}
      });
    }
    this.debug.visuals.clear();
    this.debug.scene = null;
  }

  setCannonDebugger(debuggerInstance) {
    this.cannonDebugger = debuggerInstance;
  }

  registerCollisionType(objectId, info) {
    this.collisionTypes.set(objectId, info);
  }

  getCollisionType(objectId) {
    return this.collisionTypes.get(objectId);
  }

  _createDebugForObject(objectId, mesh) {
    if (!mesh || !this.debug.scene) return;

    const body = this.bodies.get(objectId);
    if (body && body.shapes.length > 0) {
      const group = new THREE.Group();
      group.name = `${objectId}-physics-debug-group`;

      for (let i = 0; i < body.shapes.length; i++) {
        const shape = body.shapes[i];
        const offset = body.shapeOffsets?.[i];
        const orient = body.shapeOrientations?.[i];
        let geom = null;

        if (shape instanceof CANNON.Box) {
          const he = shape.halfExtents;
          geom = new THREE.BoxGeometry(he.x * 2, he.y * 2, he.z * 2);
        } else if (shape instanceof CANNON.Sphere) {
          geom = new THREE.SphereGeometry(shape.radius, 16, 12);
        } else if (shape instanceof CANNON.Cylinder) {
          const rTop = shape.radiusTop || shape.radius || 0.5;
          const rBottom = shape.radiusBottom || shape.radius || rTop;
          const height = shape.height || 1;
          geom = new THREE.CylinderGeometry(rTop, rBottom, height, 12);
        } else if (shape instanceof CANNON.Plane) {
          let width = 10,
            height = 10;
          try {
            const box = new THREE.Box3().setFromObject(mesh);
            const size = box.getSize(new THREE.Vector3());
            if (size.x > 0) width = size.x;
            if (size.y > 0) height = size.y;
            else if (size.z > 0) height = size.z;
          } catch (e) {}
          width = Math.max(0.01, width);
          height = Math.max(0.01, height);
          geom = new THREE.PlaneGeometry(width, height);
        }

        if (!geom) geom = new THREE.BoxGeometry(1, 1, 1);
        const debugMesh = new THREE.Mesh(geom, this.debug.material);
        if (offset) debugMesh.position.copy(offset);
        if (orient) debugMesh.quaternion.copy(orient);
        group.add(debugMesh);
      }

      group.position.copy(body.position);
      group.quaternion.copy(body.quaternion);
      this.debug.scene.add(group);
      this.debug.visuals.set(objectId, group);
      return;
    }

    // Fallback: bounding box
    let geometry = null;
    try {
      mesh.geometry.computeBoundingBox();
      const box = mesh.geometry.boundingBox;
      if (box) {
        const size = box.getSize(new THREE.Vector3()).multiply(mesh.scale);
        geometry = new THREE.BoxGeometry(size.x, size.y, size.z);
      }
    } catch (e) {}

    if (!geometry) {
      try {
        mesh.geometry.computeBoundingSphere();
        const radius =
          mesh.geometry.boundingSphere.radius *
          Math.max(mesh.scale.x, mesh.scale.y, mesh.scale.z);
        geometry = new THREE.SphereGeometry(radius, 16, 12);
      } catch (e) {
        geometry = new THREE.BoxGeometry(1, 1, 1);
      }
    }

    const debugMesh = new THREE.Mesh(geometry, this.debug.material);
    try {
      const pos = new THREE.Vector3();
      const quat = new THREE.Quaternion();
      mesh.getWorldPosition(pos);
      mesh.getWorldQuaternion(quat);
      debugMesh.position.copy(pos);
      debugMesh.quaternion.copy(quat);
    } catch (e) {
      debugMesh.position.copy(mesh.position);
      debugMesh.quaternion.copy(mesh.quaternion);
    }

    debugMesh.name = `${objectId}-physics-debug`;
    this.debug.scene.add(debugMesh);
    this.debug.visuals.set(objectId, debugMesh);
  }

  createPhysicsBody(objectId, mesh, physicsProperties = {}) {
    if (!this.world || !mesh) return null;
    this.removePhysicsBody(objectId);

    const isImage =
      mesh.type === 'Sprite' ||
      !!(mesh.material?.map?.image?.tagName === 'IMG');
    const isVideo =
      !!(mesh.material?.map?.image?.tagName === 'VIDEO') ||
      mesh.userData?.assetType === 'video';
    const isAudio =
      mesh.userData?.assetType === 'audio' || /audio/i.test(mesh.name || '');
    const isMediaObject = isImage || isVideo || isAudio;

    const material = this.getPhysicsMaterial(physicsProperties.physicsMaterial);
    let mass =
      typeof physicsProperties.mass === 'number'
        ? physicsProperties.mass
        : isMediaObject
          ? 1
          : 0;
    if (isMediaObject && mass === 0) mass = 1;

    const freezePos = {
      x: !!physicsProperties.freezePosition?.x,
      y: !!physicsProperties.freezePosition?.y,
      z: !!physicsProperties.freezePosition?.z,
    };
    const freezeRot = {
      x: !!physicsProperties.freezeRotation?.x,
      y: !!physicsProperties.freezeRotation?.y,
      z: !!physicsProperties.freezeRotation?.z,
    };

    let body;

    // ===== SHAPE OFFSET (geometry center vs manual COM) =====
    // Automatic: offset the collider to the visual AABB center so pivot edits
    // keep physics wrapped around the mesh, not the pivot. Body stays at the
    // pivot — do not compensate position / sync COM for this case.
    // Manual COM: legacy behavior — shape offset + body compensation so the
    // mesh does not jump when the mass origin moves.
    const isAutomaticCom =
      physicsProperties.automaticCenterOfMass !== false &&
      physicsProperties.automaticCenterOfMass !== 0;
    const pivotEdited = isPivotEditedMesh(mesh);
    const useGeometryCenterOffset = isAutomaticCom || pivotEdited;
    let shapeOffset = null;
    let compensateBodyForCom = false;

    if (useGeometryCenterOffset) {
      const geoCenter = computeGeometryCenterOffset(mesh);
      if (
        geoCenter &&
        (geoCenter.x !== 0 || geoCenter.y !== 0 || geoCenter.z !== 0)
      ) {
        shapeOffset = new CANNON.Vec3(
          geoCenter.x || 0,
          geoCenter.y || 0,
          geoCenter.z || 0
        );
      }
    } else {
      const centerOfMassData =
        physicsProperties.centerOfMass || physicsProperties.centerOfMassOffset;
      if (
        centerOfMassData &&
        (centerOfMassData.x !== 0 ||
          centerOfMassData.y !== 0 ||
          centerOfMassData.z !== 0)
      ) {
        shapeOffset = new CANNON.Vec3(
          centerOfMassData.x || 0,
          centerOfMassData.y || 0,
          centerOfMassData.z || 0
        );
        compensateBodyForCom = true;
      }
    }

    if (isMediaObject) {
      let box3 = null;
      try {
        box3 = new THREE.Box3().setFromObject(mesh);
      } catch (e) {}
      const size = box3
        ? box3.getSize(new THREE.Vector3())
        : new THREE.Vector3(1, 1, 0.01);
      const thickness = Math.max(0.005, Math.min(size.x, size.y) * 0.01);
      const boxShape = new CANNON.Box(
        new CANNON.Vec3(size.x / 2, size.y / 2, thickness / 2)
      );
      body = new CANNON.Body({ mass, material });

      // Add shape with offset if specified
      if (shapeOffset) {
        body.addShape(boxShape, shapeOffset);
      } else {
        body.addShape(boxShape);
      }
    } else {
      const shape = this.createShape(mesh, physicsProperties.colliderType);
      if (!shape) return null;
      body = new CANNON.Body({ mass, material });

      // Add shape with offset if specified
      if (shapeOffset) {
        body.addShape(shape, shapeOffset);
      } else {
        body.addShape(shape);
      }
    }

    // World transform
    try {
      const pos = new THREE.Vector3();
      const quat = new THREE.Quaternion();
      mesh.getWorldPosition(pos);
      mesh.getWorldQuaternion(quat);

      // Apply quaternion first (used for rotating any shape offset into world-space)
      body.quaternion.set(quat.x, quat.y, quat.z, quat.w);

      // Manual COM only: compensate body origin so mesh sync stays at pivot.
      // Geometry-center offsets leave the body at the pivot so the shape wraps
      // the visual mesh.
      if (compensateBodyForCom && shapeOffset) {
        const offsetThree = new THREE.Vector3(
          shapeOffset.x || 0,
          shapeOffset.y || 0,
          shapeOffset.z || 0
        );
        offsetThree.applyQuaternion(quat);
        body.position.set(
          pos.x - offsetThree.x,
          pos.y - offsetThree.y,
          pos.z - offsetThree.z
        );
      } else {
        body.position.set(pos.x, pos.y, pos.z);
      }
    } catch (e) {
      // Fallback: local transform
      body.quaternion.copy(mesh.quaternion);
      if (compensateBodyForCom && shapeOffset) {
        const offsetThree = new THREE.Vector3(
          shapeOffset.x || 0,
          shapeOffset.y || 0,
          shapeOffset.z || 0
        );
        offsetThree.applyQuaternion(mesh.quaternion);
        body.position.copy(mesh.position).sub(offsetThree);
      } else {
        body.position.copy(mesh.position);
      }
    }

    // Damping
    body.linearDamping = Math.clamp(
      Number(physicsProperties.linearDamping) || 0,
      0,
      1
    );
    body.angularDamping = Math.clamp(
      Number(physicsProperties.angularDamping) || 0,
      0,
      1
    );

    // Body type
    if (mass === 0) {
      body.type = CANNON.Body.STATIC ?? CANNON.Body.KINEMATIC;
    } else if (
      physicsProperties.isKinematic ||
      physicsProperties.useGravity === false
    ) {
      body.type = CANNON.Body.KINEMATIC;
    } else {
      body.type = CANNON.Body.DYNAMIC;
    }

    // ----- FREEZE AXES (position) -----
    body.linearFactor.set(
      freezePos.x ? 0 : 1,
      freezePos.y ? 0 : 1,
      freezePos.z ? 0 : 1
    );

    // ----- FREEZE ROTATION -----
    body.angularFactor.set(
      freezeRot.x ? 0 : 1,
      freezeRot.y ? 0 : 1,
      freezeRot.z ? 0 : 1
    );

    // ----- ZERO VELOCITY ONLY ON FROZEN AXES -----
    body.velocity.set(
      freezePos.x ? 0 : body.velocity.x,
      freezePos.y ? 0 : body.velocity.y,
      freezePos.z ? 0 : body.velocity.z
    );
    body.angularVelocity.set(
      freezeRot.x ? 0 : body.angularVelocity.x,
      freezeRot.y ? 0 : body.angularVelocity.y,
      freezeRot.z ? 0 : body.angularVelocity.z
    );

    body.userData = body.userData || {};
    body.userData.freezePosition = { ...freezePos };
    body.userData.freezeRotation = { ...freezeRot };

    // Store only manual COM for mesh sync compensation (not geometry-center offsets).
    if (compensateBodyForCom && shapeOffset) {
      body.userData.centerOfMassOffset = shapeOffset;
    }

    this.world.addBody(body);
    this.bodies.set(objectId, body);
    this.meshes.set(objectId, mesh);

    // Store initial state for resetting when exiting preview mode
    // Only store if not already stored (preserve original state from first entry to preview)
    if (!this.initialStates.has(objectId)) {
      // Store both world and local positions/rotations to handle parent-child relationships
      const worldPos = new THREE.Vector3();
      const worldQuat = new THREE.Quaternion();
      try {
        mesh.getWorldPosition(worldPos);
        mesh.getWorldQuaternion(worldQuat);
      } catch (e) {
        worldPos.copy(mesh.position);
        worldQuat.copy(mesh.quaternion);
      }

      this.initialStates.set(objectId, {
        position: {
          x: mesh.position.x,
          y: mesh.position.y,
          z: mesh.position.z,
        },
        quaternion: {
          x: mesh.quaternion.x,
          y: mesh.quaternion.y,
          z: mesh.quaternion.z,
          w: mesh.quaternion.w,
        },
        worldPosition: {
          x: worldPos.x,
          y: worldPos.y,
          z: worldPos.z,
        },
        worldQuaternion: {
          x: worldQuat.x,
          y: worldQuat.y,
          z: worldQuat.z,
          w: worldQuat.w,
        },
        scale: {
          x: mesh.scale.x,
          y: mesh.scale.y,
          z: mesh.scale.z,
        },
      });
    }

    if (this.debug.enabled && this.debug.scene) {
      this._createDebugForObject(objectId, mesh);
    }

    return body;
  }

  createShape(mesh, colliderType) {
    try {
      // Get the object's world transform
      const worldMatrix = mesh.matrixWorld.clone();
      const worldPos = new THREE.Vector3();
      const worldQuat = new THREE.Quaternion();
      const worldScale = new THREE.Vector3();
      worldMatrix.decompose(worldPos, worldQuat, worldScale);

      // Get accurate bounding box in world space (never clone objects with THREE.Audio – "Audio source type cannot be copied")
      const box3 = new THREE.Box3();
      let meshClone = null;
      const hasUncloneableAudio = (obj) => {
        if (!obj) return false;
        let found = false;
        obj.traverse((o) => {
          if (o.type === 'PositionalAudio' || o.type === 'Audio') found = true;
        });
        return found;
      };
      if (!hasUncloneableAudio(mesh)) {
        try {
          meshClone = mesh.clone();
        } catch (e) {
          // Skip clone on any other clone failure
        }
      }
      if (meshClone) {
        meshClone.position.set(0, 0, 0);
        meshClone.quaternion.set(0, 0, 0, 1);
        meshClone.scale.copy(worldScale);
        box3.setFromObject(meshClone);
      } else {
        box3.setFromObject(mesh);
      }

      // Get actual dimensions
      const size = box3.getSize(new THREE.Vector3());

      if (size.x > 0 && size.y > 0) {
        const looksLikeImage =
          mesh.type === 'Sprite' ||
          !!(
            mesh.material &&
            mesh.material.map &&
            mesh.material.map.image &&
            mesh.material.map.image.tagName === 'IMG'
          );
        const looksLikeVideo =
          !!(
            mesh.material &&
            mesh.material.map &&
            mesh.material.map.image &&
            mesh.material.map.image.tagName === 'VIDEO'
          ) || mesh.userData?.assetType === 'video';
        const isAudio = !!(
          mesh.userData &&
          (mesh.userData.assetType === 'audio' ||
            mesh.userData.isAudio ||
            /audio/i.test(mesh.name || ''))
        );
        const isMediaObject = looksLikeImage || looksLikeVideo || isAudio;

        switch (colliderType) {
          case 'Box': {
            // Create box with exact dimensions
            const halfExtents = new CANNON.Vec3(
              Math.abs(size.x / 2),
              Math.abs(size.y / 2),
              Math.abs(size.z / 2)
            );
            const box = new CANNON.Box(halfExtents);
            return box;
          }
          case 'Sphere': {
            // Use the largest dimension for consistent spherical collision
            const maxDim = Math.max(
              Math.abs(size.x),
              Math.abs(size.y),
              Math.abs(size.z)
            );
            const radius = maxDim / 2;
            return new CANNON.Sphere(radius);
          }
          case 'Capsule': {
            // Use the smallest horizontal dimension for radius
            const radius = Math.min(Math.abs(size.x / 2), Math.abs(size.z / 2));
            // Use height for capsule length
            const height = Math.abs(size.y);
            const capsule = new CANNON.Cylinder(radius, radius, height, 8);
            // Set proper orientation for capsule
            capsule.orientation = new CANNON.Quaternion();
            capsule.orientation.setFromAxisAngle(
              new CANNON.Vec3(1, 0, 0),
              Math.PI / 2
            );
            return capsule;
          }
          case 'Cylinder': {
            // Use exact dimensions for cylinder
            const radius = Math.max(Math.abs(size.x / 2), Math.abs(size.z / 2));
            const height = Math.abs(size.y);
            const cylinder = new CANNON.Cylinder(radius, radius, height, 8);
            // Align cylinder with Y axis
            cylinder.orientation = new CANNON.Quaternion();
            cylinder.orientation.setFromAxisAngle(
              new CANNON.Vec3(1, 0, 0),
              Math.PI / 2
            );
            return cylinder;
          }
          case 'Plane': {
            // Create box with exact dimensions
            const halfExtents = new CANNON.Vec3(
              Math.abs(size.x / 2),
              Math.abs(size.y / 2),
              Math.abs(size.z / 2)
            );
            const box = new CANNON.Box(halfExtents);
            return box;
          }
          case 'Mesh':
            if (mesh.geometry) {
              // Create accurate convex hull using world scale
              return this.createConvexHull(mesh.geometry, worldScale);
            }
            // Fallback to box if no geometry
            return new CANNON.Box(
              new CANNON.Vec3(
                Math.abs(size.x / 2),
                Math.abs(size.y / 2),
                Math.abs(size.z / 2)
              )
            );
          default:
            if (isMediaObject) {
              try {
                // For media objects, create a thin box matching their dimensions
                const width = Math.abs(size.x);
                const height = Math.abs(size.z && size.z > 0 ? size.z : size.y);
                // Scale thickness by the smallest dimension
                const thickness = Math.max(
                  0.005,
                  Math.min(width, height) *
                    0.01 *
                    Math.min(worldScale.x, worldScale.y, worldScale.z)
                );
                return new CANNON.Box(
                  new CANNON.Vec3(width / 2, height / 2, thickness / 2)
                );
              } catch (e) {
                // Minimum sized fallback for media objects
                const minDim = Math.min(
                  worldScale.x,
                  worldScale.y,
                  worldScale.z
                );
                const halfZ = Math.max(0.005, minDim * 0.01);
                return new CANNON.Box(
                  new CANNON.Vec3(
                    Math.max(0.5, size.x / 2),
                    Math.max(0.5, size.y / 2),
                    halfZ
                  )
                );
              }
            }
            // Default to box collider with exact dimensions
            return new CANNON.Box(
              new CANNON.Vec3(
                Math.abs(size.x / 2),
                Math.abs(size.y / 2),
                Math.abs(size.z / 2)
              )
            );
        }
      }
    } catch (e) {
      console.warn(
        `PhysicsWorld.createShape: setFromObject failed, falling back to geometry method: ${e}`
      );
    }
  }

  _boxFromGeometry(geometry, scale) {
    const sx = scale?.x ?? 1;
    const sy = scale?.y ?? 1;
    const sz = scale?.z ?? 1;

    if (geometry?.computeBoundingBox) {
      geometry.computeBoundingBox();
    }

    const box = geometry?.boundingBox;
    if (box) {
      const size = box.getSize(new THREE.Vector3());
      return new CANNON.Box(
        new CANNON.Vec3(
          Math.abs((size.x * sx) / 2),
          Math.abs((size.y * sy) / 2),
          Math.abs((size.z * sz) / 2)
        )
      );
    }

    return new CANNON.Box(new CANNON.Vec3(0.5, 0.5, 0.5));
  }

  createConvexHull(geometry, scale) {
    const positions = geometry?.attributes?.position;
    if (!positions || positions.count < 4) {
      return this._boxFromGeometry(geometry, scale);
    }

    const sx = scale?.x ?? 1;
    const sy = scale?.y ?? 1;
    const sz = scale?.z ?? 1;
    const maxPoints = 64;
    const step = Math.max(1, Math.floor(positions.count / maxPoints));
    const seen = new Set();
    const points = [];

    for (let i = 0; i < positions.count; i += step) {
      const x = positions.getX(i) * sx;
      const y = positions.getY(i) * sy;
      const z = positions.getZ(i) * sz;
      const key = `${x.toFixed(4)}|${y.toFixed(4)}|${z.toFixed(4)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      points.push(new THREE.Vector3(x, y, z));
    }

    if (points.length < 4) {
      return this._boxFromGeometry(geometry, scale);
    }

    try {
      const hull = new ConvexHull().setFromPoints(points);
      if (!hull.faces?.length || !hull.vertices?.length) {
        return this._boxFromGeometry(geometry, scale);
      }

      const vertexMap = new Map();
      const vertices = [];

      hull.vertices.forEach((node) => {
        vertexMap.set(node, vertices.length);
        vertices.push(
          new CANNON.Vec3(node.point.x, node.point.y, node.point.z)
        );
      });

      const faces = [];
      for (let i = 0; i < hull.faces.length; i++) {
        const face = hull.faces[i];
        const indices = [];
        let edge = face.edge;

        do {
          const vertexNode = edge.head();
          const index = vertexMap.get(vertexNode);
          if (index === undefined) break;
          indices.push(index);
          edge = edge.next;
        } while (edge !== face.edge && indices.length < 32);

        if (indices.length >= 3) {
          faces.push(indices);
        }
      }

      if (vertices.length < 4 || faces.length < 4) {
        return this._boxFromGeometry(geometry, scale);
      }

      return new CANNON.ConvexPolyhedron({ vertices, faces });
    } catch (e) {
      console.warn(
        'PhysicsWorld.createConvexHull failed, using box fallback:',
        e
      );
      return this._boxFromGeometry(geometry, scale);
    }
  }

  getPhysicsMaterial(name) {
    return this.materials.get(name) || this.materials.get('Default');
  }

  updatePhysicsBody(objectId, physicsProperties) {
    const body = this.bodies.get(objectId);
    if (!body) return;

    const mesh = this.meshes.get(objectId);

    const fp = {
      x: !!physicsProperties.freezePosition?.x,
      y: !!physicsProperties.freezePosition?.y,
      z: !!physicsProperties.freezePosition?.z,
    };
    const fr = {
      x: !!physicsProperties.freezeRotation?.x,
      y: !!physicsProperties.freezeRotation?.y,
      z: !!physicsProperties.freezeRotation?.z,
    };

    // Apply freeze factors
    body.linearFactor.set(fp.x ? 0 : 1, fp.y ? 0 : 1, fp.z ? 0 : 1);
    body.angularFactor.set(fr.x ? 0 : 1, fr.y ? 0 : 1, fr.z ? 0 : 1);

    // Zero velocity ONLY on frozen axes
    body.velocity.set(
      fp.x ? 0 : body.velocity.x,
      fp.y ? 0 : body.velocity.y,
      fp.z ? 0 : body.velocity.z
    );
    body.angularVelocity.set(
      fr.x ? 0 : body.angularVelocity.x,
      fr.y ? 0 : body.angularVelocity.y,
      fr.z ? 0 : body.angularVelocity.z
    );

    body.userData.freezePosition = fp;
    body.userData.freezeRotation = fr;

    // Update mass / material / type
    body.mass =
      typeof physicsProperties.mass === 'number'
        ? physicsProperties.mass
        : body.mass;
    body.updateMassProperties();
    body.material = this.getPhysicsMaterial(physicsProperties.physicsMaterial);

    if (body.mass === 0) {
      body.type = CANNON.Body.STATIC ?? CANNON.Body.KINEMATIC;
    } else if (physicsProperties.isKinematic || !physicsProperties.useGravity) {
      body.type = CANNON.Body.KINEMATIC;
    } else {
      body.type = CANNON.Body.DYNAMIC;
    }

    // ===== Update center of mass offset if changed =====
    const centerOfMassUpdate =
      physicsProperties.centerOfMass ?? physicsProperties.centerOfMassOffset;
    const automaticComUpdate = physicsProperties.automaticCenterOfMass;
    const isAutomaticCom =
      automaticComUpdate !== false && automaticComUpdate !== 0;
    const pivotEdited = isPivotEditedMesh(mesh);
    const useGeometryCenterOffset = isAutomaticCom || pivotEdited;

    if (centerOfMassUpdate !== undefined || automaticComUpdate !== undefined) {
      if (
        !useGeometryCenterOffset &&
        centerOfMassUpdate &&
        (centerOfMassUpdate.x !== 0 ||
          centerOfMassUpdate.y !== 0 ||
          centerOfMassUpdate.z !== 0)
      ) {
        body.userData.centerOfMassOffset = new CANNON.Vec3(
          centerOfMassUpdate.x || 0,
          centerOfMassUpdate.y || 0,
          centerOfMassUpdate.z || 0
        );
      } else {
        delete body.userData.centerOfMassOffset;
      }
    }

    // Recreate if collider / COM / automatic mode changed (geometry center may move after pivot edit)
    const hasCenterOfMassChange =
      physicsProperties.centerOfMass !== undefined ||
      physicsProperties.centerOfMassOffset !== undefined ||
      physicsProperties.automaticCenterOfMass !== undefined ||
      mesh?.userData?.pivotEdited;
    if (
      mesh &&
      (physicsProperties.colliderType ||
        physicsProperties.mass !== undefined ||
        hasCenterOfMassChange)
    ) {
      const oldPos = body.position.clone();
      const oldQuat = body.quaternion.clone();
      this.removePhysicsBody(objectId);
      const newBody = this.createPhysicsBody(objectId, mesh, physicsProperties);
      if (newBody) {
        if (hasCenterOfMassChange || physicsProperties.colliderType) {
          try {
            const worldPos = new THREE.Vector3();
            const worldQuat = new THREE.Quaternion();
            mesh.getWorldPosition(worldPos);
            mesh.getWorldQuaternion(worldQuat);

            newBody.quaternion.set(
              worldQuat.x,
              worldQuat.y,
              worldQuat.z,
              worldQuat.w
            );

            const isAuto =
              physicsProperties.automaticCenterOfMass !== false &&
              physicsProperties.automaticCenterOfMass !== 0;
            const useGeometryCenterOffset = isAuto || isPivotEditedMesh(mesh);
            if (useGeometryCenterOffset) {
              // Geometry-centered shape: body stays at pivot
              newBody.position.set(worldPos.x, worldPos.y, worldPos.z);
            } else {
              const cm =
                physicsProperties.centerOfMass ||
                physicsProperties.centerOfMassOffset;
              if (cm && (cm.x !== 0 || cm.y !== 0 || cm.z !== 0)) {
                const shapeOffset = new THREE.Vector3(
                  cm.x || 0,
                  cm.y || 0,
                  cm.z || 0
                );
                shapeOffset.applyQuaternion(worldQuat);
                newBody.position.set(
                  worldPos.x - shapeOffset.x,
                  worldPos.y - shapeOffset.y,
                  worldPos.z - shapeOffset.z
                );
              } else {
                newBody.position.set(worldPos.x, worldPos.y, worldPos.z);
              }
            }
          } catch (e) {
            newBody.position.copy(oldPos);
            newBody.quaternion.copy(oldQuat);
          }
        } else {
          newBody.position.copy(oldPos);
          newBody.quaternion.copy(oldQuat);
        }
      }
    }
  }

  removePhysicsBody(objectId) {
    const body = this.bodies.get(objectId);
    if (body && this.world) this.world.removeBody(body);

    const debugMesh = this.debug.visuals.get(objectId);
    if (debugMesh && this.debug.scene) {
      try {
        this.debug.scene.remove(debugMesh);
      } catch (e) {}
    }
    this.debug.visuals.delete(objectId);
    this.collisionTypes.delete(objectId);

    if (this.cannonDebugger) {
      try {
        this.cannonDebugger.remove?.(objectId);
      } catch (e) {}
      try {
        this.cannonDebugger.removeBody?.(body);
      } catch (e) {}
      try {
        this.cannonDebugger.update?.();
      } catch (e) {}
    }

    this.bodies.delete(objectId);
    this.meshes.delete(objectId);
    // Don't delete initialStates here - we need them for reset when exiting preview
  }

  clearAllBodies() {
    this.bodies.forEach((body) => this.world?.removeBody(body));
    if (this.debug.scene) {
      this.debug.visuals.forEach((v) => {
        try {
          this.debug.scene.remove(v);
        } catch (e) {}
      });
    }
    this.debug.visuals.clear();
    this.bodies.clear();
    this.meshes.clear();
    this.initialStates.clear();
  }

  /** Clear simulation bodies for preview replay without disabling the world. */
  resetBodiesForReplay() {
    this.clearAllBodies();
  }

  /**
   * Restore all objects to their initial positions/rotations
   * Called when exiting preview mode to reset objects to design state
   */
  restoreInitialStates() {
    // Restore all meshes that have initial states stored
    this.initialStates.forEach((initialState, objectId) => {
      const mesh = this.meshes.get(objectId);
      if (!initialState || !mesh) return;

      try {
        // Restore position
        mesh.position.set(
          initialState.position.x,
          initialState.position.y,
          initialState.position.z
        );

        // Restore rotation
        mesh.quaternion.set(
          initialState.quaternion.x,
          initialState.quaternion.y,
          initialState.quaternion.z,
          initialState.quaternion.w
        );

        // Restore scale (in case it was modified)
        if (initialState.scale) {
          mesh.scale.set(
            initialState.scale.x,
            initialState.scale.y,
            initialState.scale.z
          );
        }

        // Force matrix update to make changes visible
        mesh.updateMatrix();
        mesh.updateMatrixWorld(true);

        console.log(
          `PhysicsWorld: Restored initial state for object: ${objectId}`
        );
      } catch (error) {
        console.error(
          `PhysicsWorld: Error restoring initial state for ${objectId}:`,
          error
        );
      }
    });

    // Clear initial states after restoring so they can be re-stored on next preview entry
    this.initialStates.clear();
  }

  setGravity(x, y, z) {
    if (!this.world) return;
    const gx = typeof x === 'number' ? x : 0;
    const gy = typeof y === 'number' ? y : -9.82;
    const gz = typeof z === 'number' ? z : 0;
    this.world.gravity.set(gx, gy, gz);
    this._lastGravity = { x: gx, y: gy, z: gz };
  }

  getGravity() {
    return (
      this._lastGravity ||
      (this.world?.gravity
        ? {
            x: this.world.gravity.x,
            y: this.world.gravity.y,
            z: this.world.gravity.z,
          }
        : { x: 0, y: -9.82, z: 0 })
    );
  }

  getPhysicsBody(id) {
    return this.bodies.get(id);
  }
  getMesh(id) {
    return this.meshes.get(id);
  }
  hasPhysics(id) {
    return this.bodies.has(id);
  }

  resetBody(
    objectId,
    newPos = { x: 0, y: 10, z: 0 },
    newQuat = { x: 0, y: 0, z: 0, w: 1 }
  ) {
    const body = this.bodies.get(objectId);
    if (!body) return;

    const fp = body.userData?.freezePosition || {
      x: false,
      y: false,
      z: false,
    };
    const fr = body.userData?.freezeRotation || {
      x: false,
      y: false,
      z: false,
    };

    // Reset velocities completely (good practice on teleport)
    body.velocity.set(0, 0, 0);
    body.angularVelocity.set(0, 0, 0);

    // Only overwrite position components that are NOT frozen
    if (!fp.x) body.position.x = newPos.x;
    if (!fp.y) body.position.y = newPos.y;
    if (!fp.z) body.position.z = newPos.z;

    // Only overwrite quaternion components that are NOT frozen
    if (!fr.x) body.quaternion.x = newQuat.x;
    if (!fr.y) body.quaternion.y = newQuat.y;
    if (!fr.z) body.quaternion.z = newQuat.z;
    if (!fr.x && !fr.y && !fr.z) body.quaternion.w = newQuat.w;

    body.quaternion.normalize();
  }

  destroy() {
    this.clearAllBodies();
    this.materials.clear();
    this.world = null;
  }
}

// Singleton
export const physicsWorld = new PhysicsWorld();

// Expose for quick debugging in development
try {
  if (
    typeof window !== 'undefined' &&
    process &&
    process.env &&
    process.env.NODE_ENV !== 'production'
  ) {
    window.physicsWorld = physicsWorld;
  }
} catch (e) {
  // ignore
}
